import { cn } from '@pubstack/ui/utils/cn'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { cloneElement, FunctionComponent, ReactElement, ReactNode, RefObject, useEffect, useRef, useState } from 'react'
import { PopoverPosition } from 'react-tiny-popover'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { AlwaysVisibleScrollbarStyle, ScrollbarWidth } from '~/assets/style/utils'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { WithClassName } from '~/types/utils'
import { Icon } from './Icon'
import { Popover } from './Popover'
import { Status, StatusState } from './Status'
import { Tooltip } from './Tooltip'

export type SelectOptionProp<T = unknown> = {
  value: T
  label: string
  trackingLabel: TrackingLabel
  status?: StatusState
  sublabel?: string
  disabled?: boolean
  /**
   * Why this option is closed, shown on hover rather than printed under the label.
   *
   * `sublabel` is the other way of saying something about an option, and it stays for the things worth
   * reading on every option at once — "3 sites mapped" and the like. A rule that only applies to the
   * options you cannot pick is not that: printed inline it lengthens every closed row in a list the
   * reader is scanning for the open ones.
   */
  disabledReason?: string
}

type SelectPopoverProps = WithClassName & {
  onChange?: (option: SelectOptionProp) => unknown
  filterSection?: ReactNode
  options: SelectOptionProp[]
  optionsLoading?: boolean
  trigger: ReactElement<{ isOpened?: boolean; className?: string; ref: RefObject<HTMLElement | null> }>
  open: boolean
  setOpen: (open: boolean) => void
  allowMultipleSelection?: boolean
  showMultiLevelAsCategory?: boolean
  OptionContentComponent?: FunctionComponent<{
    option: SelectOptionProp<unknown>
  }>
  maxWidthPopoverSameAsTrigger?: boolean
  positions?: PopoverPosition[]
  footerOption?: ReactNode
}

const Option = styled.div<{ disabled?: boolean }>`
  --item__bg--hover: ${Colors.Topaze};
  --item__bg--active: ${Colors.King};
  --item__bg: ${Colors.White};
  ${Fonts.P1};
  ${Fonts.colors.Jet}
  padding: 8px 12px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;

  background: var(--item__bg);

  &:last-child {
    margin-bottom: 0;
  }

  ${({ disabled }) =>
    disabled
      ? css`
          font-style: italic;
          color: ${Colors.Ash};
        `
      : css`
          cursor: pointer;

          :hover {
            background: var(--item__bg--hover);
          }

          :active {
            color: ${Colors.White};
            background: var(--item__bg--active);
          }
        `}
`

const NoResult = styled.div`
  ${Fonts.P1};
  ${Fonts.colors.Hurricane}
  padding: 8px 12px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-style: italic;
  background: ${Colors.White};
  margin-bottom: 0;
`

const OptionPopover = styled(Popover)`
  margin-left: calc(${ScrollbarWidth} + 4px);
`

const OptionWithSublabel = styled.div`
  display: flex;
  flex-direction: column;
`

const OptionWithStatus = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: baseline;
`

const Sublabel = styled.span`
  ${Fonts.P3};
  ${Fonts.colors.SlateGrey};
`

const SelectOptions = styled.div`
  border-radius: 4px;
  padding: 0;
  max-height: 272px;
  overflow-y: auto;
  box-sizing: border-box;
  ${AlwaysVisibleScrollbarStyle}
`

const Category = styled.div`
    height: 28px;
    padding: 4px 12px;
    ${Fonts.P3};
    ${Fonts.colors.SlateGrey};
    display: flex;
    align-items: center;
    gap: 4px;

    :after {
      display: inline-block;
      height: 0.5px;
      content: "";
      flex: 1 0 auto;
      background-color: ${Colors.SlateGrey};
    }
    
    :first-of-type {
      margin-top: 4px;
    }
`

const FilterSectionWrapper = styled.div<{ minWidth: number; maxWidthPopoverSameAsTrigger?: boolean }>`
  min-width: ${({ minWidth }) => minWidth}px;
  max-width: ${({ maxWidthPopoverSameAsTrigger }) => (maxWidthPopoverSameAsTrigger ? '100%' : 'none')};
`

const Hr = styled.hr`
  width: 100%;
  border: none;
  border-top: 1px solid ${Colors.Platinum};
  margin: 0;
`

const selectPopoverTriggerClassName = 'select-popover-trigger'
const findElement = (e: HTMLElement | null): HTMLElement | undefined => {
  if (!e || e === document.body) return undefined
  return e.classList.contains(selectPopoverTriggerClassName) ? e : findElement(e.parentElement)
}

const OptionContent: FunctionComponent<{
  option: SelectOptionProp<unknown>
}> = ({ option }) => {
  const content = (
    <OptionWithStatus>
      {option.status && <Status state={option.status} />}
      <OptionWithSublabel>
        {option.label}
        <Sublabel>{option.sublabel}</Sublabel>
      </OptionWithSublabel>
    </OptionWithStatus>
  )
  return option.disabledReason ? (
    <Tooltip title={option.disabledReason} align={'start'}>
      {content}
    </Tooltip>
  ) : (
    content
  )
}

const BaseSelectableOptionsPopover: FunctionComponent<SelectPopoverProps> = ({
  className,
  positions,
  filterSection,
  options,
  optionsLoading,
  onChange,
  trigger,
  open,
  setOpen,
  allowMultipleSelection,
  showMultiLevelAsCategory,
  OptionContentComponent = OptionContent,
  maxWidthPopoverSameAsTrigger,
  footerOption,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null)
  const dropdownRefForSubOptions = useRef<HTMLDivElement>(null)

  const triggerRef = useRef<HTMLElement>(null)
  const [width, setWidth] = useState(0)
  const setter = () => {
    const element = triggerRef.current ? findElement(triggerRef.current) : undefined
    setWidth(element?.clientWidth || 0)
  }
  const [currentHover, setCurrentHover] = useState<number | undefined>(undefined)
  const [canHover, setCanHover] = useState(false)

  useEffect(() => {
    setter()
  })

  useEffect(() => {
    const listener = () => setTimeout(setter, 50)
    window.addEventListener('resize', listener)
    return () => {
      window.removeEventListener('resize', listener)
    }
  }, [])

  const onOptionClick = (option: SelectOptionProp) => {
    if (option.disabled) {
      return
    }

    onChange && onChange(option)
    setCurrentHover(undefined)
    if (!allowMultipleSelection) {
      setOpen(false)
    }
  }

  useEffect(() => {
    if (open) {
      // ReactTinyPopover renders dropdown in front of input before translate it so cursor hover it few seconds that could open a sub-list
      setTimeout(() => setCanHover(true), 100)
    }
    // oxlint-disable-next-line react/set-state-in-effect -- paired with the hover delay above: both reset the hover state for the same open/close transition
    setCurrentHover(undefined)
  }, [open])

  return (
    <Popover
      // oxlint-disable-next-line react/refs -- attaching a ref to an arbitrary element requires cloneElement; the compiler reads it as a ref access during render and skips memoizing this component. Callback refs and hoisting the props object were both tried and bail out the same way.
      trigger={cloneElement(trigger, {
        className: cn([className, selectPopoverTriggerClassName]),
        ref: triggerRef,
      })}
      open={open}
      setOpen={setOpen}
      isToggleable={allowMultipleSelection}
      positions={positions}
    >
      {filterSection && (
        <>
          <FilterSectionWrapper onClick={(e) => e.stopPropagation()} minWidth={width} maxWidthPopoverSameAsTrigger={maxWidthPopoverSameAsTrigger}>
            {filterSection}
          </FilterSectionWrapper>
          <Hr />
        </>
      )}
      <SelectOptions
        style={{
          minWidth: width,
          maxWidth: maxWidthPopoverSameAsTrigger ? width : '100%',
        }}
        ref={dropdownRef}
      >
        {optionsLoading ? (
          <NoResult>Loading...</NoResult>
        ) : options.length ? (
          (options || []).map((option, optionIndex) =>
            option.value instanceof Array ? (
              showMultiLevelAsCategory ? (
                <>
                  <Category key={`category.${optionIndex}`}>{option.label}</Category>
                  {option.value.map((subOption, subOptionIndex) => (
                    <Option data-tracking={subOption.trackingLabel} key={`${optionIndex}subOption${subOptionIndex}`} onClick={() => onOptionClick(subOption)} disabled={subOption.disabled}>
                      <OptionContentComponent option={subOption} />
                    </Option>
                  ))}
                </>
              ) : (
                <OptionPopover
                  key={`popover.${optionIndex}`}
                  open={optionIndex === currentHover}
                  trigger={
                    <Option
                      key={optionIndex}
                      onMouseEnter={() => {
                        if (canHover) {
                          setCurrentHover(optionIndex)
                        }
                      }}
                      disabled={option.disabled}
                    >
                      <OptionContentComponent option={option} />
                      <Icon name={'chevron_right'} />
                    </Option>
                  }
                  positions={['right', 'left']}
                >
                  <SelectOptions style={{ minWidth: width + 20, width: '100%' }} ref={dropdownRefForSubOptions}>
                    {option.value.map((subOption, subOptionIndex) => (
                      <Option
                        data-tracking={subOption.trackingLabel}
                        key={`${optionIndex}subOption${subOptionIndex}`}
                        onClick={() => {
                          onOptionClick(subOption)
                        }}
                        disabled={subOption.disabled}
                      >
                        <OptionContentComponent option={subOption} />
                      </Option>
                    ))}
                  </SelectOptions>
                </OptionPopover>
              )
            ) : (
              <Option
                data-tracking={option.trackingLabel}
                key={optionIndex}
                onClick={() => onOptionClick(option)}
                onMouseEnter={() => {
                  if (canHover) {
                    setCurrentHover(optionIndex)
                  }
                }}
                disabled={option.disabled}
              >
                <OptionContentComponent option={option} />
              </Option>
            )
          )
        ) : (
          <NoResult>No results match your search.</NoResult>
        )}
        {footerOption}
      </SelectOptions>
    </Popover>
  )
}
export const SelectableOptionsPopover = styled(BaseSelectableOptionsPopover)``
